// Implements: F-UIX-021, F-VER-011
/**
 * The hierarchy of `parent_version_id`, built from a flat list.
 *
 * The API promises no order, so the order is decided here: roots and
 * siblings by `sequence_number`, which is the order the versions were made.
 * A node whose parent is not in the list is a ROOT, not an omission: dropping
 * it would hide a version because of a pointer the screen cannot resolve, and
 * the purge rule that keeps ancestors (F-VER-017) is the backend's, not
 * something this file may assume holds.
 */

import type { VersionTreeNode } from "../../../types/api";

export interface VersionBranch {
  readonly node: VersionTreeNode;
  readonly children: readonly VersionBranch[];
}

function bySequence(left: VersionTreeNode, right: VersionTreeNode): number {
  return left.sequence_number - right.sequence_number;
}

export function buildVersionForest(nodes: readonly VersionTreeNode[]): readonly VersionBranch[] {
  const known = new Set(nodes.map((node) => node.id));
  const childrenOf = new Map<string, VersionTreeNode[]>();
  const roots: VersionTreeNode[] = [];

  for (const node of nodes) {
    const parent = node.parent_version_id;
    if (parent === null || !known.has(parent)) {
      roots.push(node);
      continue;
    }
    childrenOf.set(parent, [...(childrenOf.get(parent) ?? []), node]);
  }

  // A cycle would never reach a root and so never be drawn; `seen` also keeps
  // a self-referencing pointer from recursing for ever.
  const seen = new Set<string>();
  const branch = (node: VersionTreeNode): VersionBranch => {
    seen.add(node.id);
    const children = (childrenOf.get(node.id) ?? [])
      .filter((child) => !seen.has(child.id))
      .sort(bySequence)
      .map(branch);
    return { node, children };
  };

  return roots.sort(bySequence).map(branch);
}
